import { fetchGraphQLQuery } from '@updater-core/lib/graphql';
import { serviceabilityPresenter, trimOfferForReservation } from './utils';
import { createLogger } from './logger';
import { MEASURE, TRACK, ERROR } from '@updater-core/lib/tracker';
import gql from 'graphql-tag';

const { log } = createLogger();

export const gateway = ({ sessionHeaders, env, moveId, tracker }) => {
  const graphQLQuery = ({ query, variables }) =>
    fetchGraphQLQuery({
      headers: sessionHeaders,
      url: `${env.endpoints.api.url}/graphql`,
      query,
      variables,
    });

  const getPropertyConfig = (moveId) =>
    graphQLQuery({
      query: gql`
        query homeServicesPropertyConfig($input: ID!) {
          homeServicesPropertyConfig(moveId: $input) {
            propertyConfig {
              allowSatellite
              providers {
                featuredKind
                exclusive
                id
                logo
                name
              }
            }
          }
        }
      `,
      variables: {
        input: moveId,
      },
    }).then((result) => ({
      propertyConfig: result?.data?.homeServicesPropertyConfig?.propertyConfig,
    }));

  const createOfferFragment = () => {
    const NAME = 'offer';
    const FRAGMENT = `fragment ${NAME} on HomeServicesOffer {
      id
      code
      name
      price
      valueScore
      valueTier
      term
      introductoryPrice {
        price
        term
        duration
        savingsPercent
      }
      services {
        internet {
          kind
          speed {
            tier
            mbps
          }
        }
        tv {
          additionalLanguages
          kind
          channels {
            count
          }
        }
        phone {
          kind
        }
      }
      provider {
        id
        featured
        exclusive
        featuredKind
        logo
        name
      }
    }
    `;

    return {
      fragment: FRAGMENT,
      name: NAME,
    };
  };

  const offerFragment = createOfferFragment();

  const getReservation = ({ moveId }) =>
    graphQLQuery({
      query: gql`
        query reservation($moveId: ID!) {
          homeServicesReservation(moveId: $moveId) {
            reservation {
              id
              code
              offer {
                ...${offerFragment.name}
              }
              offers {
                ...${offerFragment.name}
              }
              status
              user { email firstName lastName phone }
              toAddress { street unit city state postalCode }
              preferences { flow }
            }
          }
        }

        ${offerFragment.fragment}
      `,
      variables: {
        moveId,
      },
    }).then((result) => ({
      // Don’t pass through nulls
      reservation:
        result?.data?.homeServicesReservation?.reservation || undefined,
    }));

  const createReservation = ({
    offer,
    offers,
    preferences,
    propertyConfig,
    metadata,
    callCenter,
    user,
    toAddress,
    checkoutEnabled,
  }) =>
    graphQLQuery({
      query: gql`
        mutation makeHomeServicesReservation($input: MakeHomeServicesReservationInput!){
          makeHomeServicesReservation(input: $input) {
            reservation {
              id
              code
              offer {
                ...${offerFragment.name}
              }
              offers {
                ...${offerFragment.name}
              }
              status
              user { email firstName lastName phone }
              toAddress { street unit city state postalCode }
              preferences { flow }
            }
          }
        }
        ${offerFragment.fragment}
      `,
      variables: {
        input: {
          offer: trimOfferForReservation(offer),
          offers: offers?.map(trimOfferForReservation),
          preferences: {
            flow: preferences.flow,
            tv: preferences.tv,
            phone: preferences.phone,
            internet: preferences.internet,
            propertyConfig: preferences.propertyConfig,
          },
          propertyConfig,
          ...(metadata?.bridgevine?.sessionId &&
          metadata?.bridgevine?.transactionId
            ? { metadata }
            : {}),
          moveId,
          callCenter,
          user,
          toAddress,
          checkoutEnabled,
        },
      },
    }).then((result) => {
      if (!result || result.errors) {
        tracker.track({
          verb: ERROR,
          object: 'graphql_reservation_response_errors',
          details: {
            errors: result?.errors,
          },
        });

        return {};
      }

      return {
        // Don’t pass through nulls
        reservation:
          result?.data?.makeHomeServicesReservation?.reservation || undefined,
      };
    });

  const trackErrors = (response) => {
    if (!response) {
      tracker.track({
        verb: ERROR,
        object: 'invalid_graphql_response',
        details: {
          response,
        },
      });
    } else if (response.errors) {
      if (
        response.errors.some((error) =>
          error?.message?.match?.(/(504|Time-out|Timeout)/)
        )
      ) {
        tracker.track({
          verb: ERROR,
          object: 'graphql_timeout_error',
          details: {
            errors: response.errors,
          },
        });
      } else if (
        response.errors.some((error) =>
          error?.message?.match?.(/Invalid login credentials/)
        )
      ) {
        tracker.track({
          verb: ERROR,
          object: 'graphql_unauthorized_error',
          details: {
            errors: response.errors,
          },
        });
      } else {
        tracker.track({
          verb: ERROR,
          object: 'graphql_response_errors',
          details: {
            errors: response.errors,
          },
        });
      }
    }
  };

  const queryAvailableOffers = ({ propertyConfig }) =>
    graphQLQuery({
      query: gql`
        query availableOffers($input: AvailableOffersInput!) {
          availableOffers(input: $input) {
            offers {
              code
              providerCode
              name
              disclaimer
              disclaimers {
                disclaimer
                displayLocation
                referenceMark
              }
              highlights
              extendedHighlights
              promotions {
                description
                disclaimer {
                  disclaimer
                  displayLocation
                  referenceMark
                }
                name
              }
              pricing {
                price
                charges {
                  amount
                  description
                  type
                  term {
                    unit
                    length
                  }
                }
                term {
                  unit
                  length
                }
              }
              tags
              ... on TvInternetOffer {
                products {
                  internet {
                    name
                    downloadSpeed {
                      value
                      unit
                    }
                    uploadSpeed {
                      value
                      unit
                    }
                    type
                    features
                  }
                  phone {
                    name
                    type
                    features
                  }
                  tv {
                    name
                    channels {
                      count
                      lineupCodes
                    }
                    features
                    additionalLanguages
                    type
                  }
                }
              }
              metadata {
                fromCache
                cachedAt
              }
            }
            request {
              id
              address {
                street
                unit
                city
                state
                postalCode
              }
            }
            providerSummaries {
              availability
              queryAddress {
                street
                unit
                city
                state
                postalCode
              }
              addressSuggestions {
                street
                unit
                city
                state
                postalCode
              }
              status
              error {
                code
                message
              }
              provider {
                id
                code
                name
                featured
                exclusive
                displayName
                url
                phoneNumber
                logo {
                  url
                }
                validationSteps
                config {
                  supportExistingCustomer
                  supportAddressSuggestions
                }
              }
            }
          }
        }
      `,
      variables: {
        input: {
          moveId,
        },
      },
    }).then(async (response) => {
      if (!response || response.errors) {
        trackErrors(response);

        return undefined;
      }

      try {
        const data = response.data?.availableOffers;
        const transformedData = await serviceabilityPresenter({
          ...data,
          propertyConfig,
        });

        log('New serviceability data', data, 'mapped to', transformedData);

        return transformedData;
      } catch (e) {
        const { message, stack, name } = e;

        tracker.track({
          verb: ERROR,
          object: 'new_serviceability_mapping',
          details: {
            message,
            stack,
            name,
            value: e?.valueOf(),
            str: e?.toString(),
          },
        });

        return undefined;
      }
    });

  const trackWaitTime = async (fn, args) => {
    if (window.performance) {
      const start = performance.now();
      const response = await fn(...args);
      const end = performance.now();

      const waitTime = end - start;

      tracker.track({
        verb: MEASURE,
        object: 'search_offers_wait_time',
        details: {
          waitTime,
        },
      });

      return response;
    }

    return fn(...args);
  };

  const trackMissingServices = (request) => {
    request.then((response) => {
      if (!response || response.errors) {
        return;
      }

      try {
        const hasInternet =
          response.offers?.length &&
          response.offers.findIndex((offer) => offer?.services?.internet) > -1;

        const hasTv =
          response.offers?.length &&
          response.offers.findIndex((offer) => offer?.services?.tv) > -1;

        const hasPhone =
          response.offers?.length &&
          response.offers.findIndex((offer) => offer?.services?.phone) > -1;

        if (!hasInternet) {
          tracker.track({
            verb: TRACK,
            object: 'no_internet_offers',
          });
        }

        if (!hasTv) {
          tracker.track({
            verb: TRACK,
            object: 'no_tv_offers',
          });
        }

        if (!hasPhone) {
          tracker.track({
            verb: TRACK,
            object: 'no_phone_offers',
          });
        }
      } catch (e) {
        tracker.track({
          verb: ERROR,
          object: 'services_check',
          details: {
            errors: JSON.stringify(e),
          },
        });
      }
    });

    return request;
  };

  const searchOffers = (...args) => {
    return trackMissingServices(
      trackWaitTime(queryAvailableOffers, args)
    );
  };

  const refreshOffersCache = async () => {
    console.log(
      'Making availableOffers query with cachingStrategy set to none'
    );
    const response = await graphQLQuery({
      query: gql`
        query availableOffers($input: AvailableOffersInput!) {
          availableOffers(input: $input) {
            offers {
              code
              name
            }
            request {
              id
              address {
                street
                unit
                city
                state
                postalCode
              }
            }
            providerSummaries {
              availability
              status
              provider {
                id
                code
                featured
                exclusive
                displayName
              }
            }
          }
        }
      `,
      variables: {
        input: {
          moveId,
          cachingStrategy: 'none',
        },
      },
    });
    console.log('Offers cache refreshed', response);
  };

  return {
    getReservation,
    getPropertyConfig,
    searchOffers,
    createReservation,
    refreshOffersCache,
  };
};
